import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
    Loader2, 
    Plus, 
    Ticket,
    Copy,
    Trash2,
    RefreshCw,
    Clock,
    Users,
    Check,
    X,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate, cn } from '../../lib/utils';
import { toast } from 'sonner';

const AdminInviteCodes = () => {
    const [loading, setLoading] = useState(true);
    const [codes, setCodes] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showBulkForm, setShowBulkForm] = useState(false);
    const [creating, setCreating] = useState(false);
    const [expandedCode, setExpandedCode] = useState(null);
    const [newCode, setNewCode] = useState({
        code: '',
        uses_limit: 1,
        expiration_date: '',
        note: ''
    });
    const [bulkData, setBulkData] = useState({
        count: 20,
        prefix: 'BETA',
        uses_limit: 1,
        expiration_date: '',
        note: ''
    });
    const [bulkResults, setBulkResults] = useState(null);

    useEffect(() => {
        loadCodes();
    }, []);

    const loadCodes = async () => {
        try {
            const response = await adminAPI.getInviteCodes();
            setCodes(response.data);
        } catch (err) {
            console.error('Failed to load invite codes:', err);
            toast.error('Failed to load invite codes');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreating(true);

        try {
            const data = {
                code: newCode.code || undefined,
                uses_limit: newCode.uses_limit === 0 ? null : newCode.uses_limit,
                expiration_date: newCode.expiration_date || null,
                note: newCode.note
            };

            const response = await adminAPI.createInviteCode(data);
            toast.success(`Invite code ${response.data.code} created!`);
            setShowCreateForm(false);
            setNewCode({ code: '', uses_limit: 1, expiration_date: '', note: '' });
            loadCodes();
        } catch (err) {
            console.error('Failed to create invite code:', err);
            toast.error(err.response?.data?.detail || 'Failed to create invite code');
        } finally {
            setCreating(false);
        }
    };

    const handleDisable = async (codeId, codeName) => {
        if (!window.confirm(`Are you sure you want to disable code ${codeName}?`)) return;

        try {
            await adminAPI.disableInviteCode(codeId);
            toast.success('Invite code disabled');
            loadCodes();
        } catch (err) {
            console.error('Failed to disable code:', err);
            toast.error('Failed to disable code');
        }
    };

    const handleBulkCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        setBulkResults(null);

        try {
            const data = {
                count: bulkData.count,
                prefix: bulkData.prefix || undefined,
                uses_limit: bulkData.uses_limit,
                expiration_date: bulkData.expiration_date || null,
                note: bulkData.note
            };

            const response = await adminAPI.createBulkInviteCodes(data);
            toast.success(`Created ${response.data.created_count} invite codes!`);
            setBulkResults(response.data.codes);
            loadCodes();
        } catch (err) {
            console.error('Failed to create bulk codes:', err);
            toast.error(err.response?.data?.detail || 'Failed to create codes');
        } finally {
            setCreating(false);
        }
    };

    const copyAllCodes = () => {
        if (!bulkResults) return;
        const codeList = bulkResults.map(c => c.code).join('\n');
        navigator.clipboard.writeText(codeList);
        toast.success('All codes copied to clipboard');
    };

    const copyToClipboard = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Code copied to clipboard');
    };

    const getUsageLabel = (code) => {
        if (code.uses_limit === null) return 'Unlimited';
        return `${code.uses_count}/${code.uses_limit}`;
    };

    const getStatusBadge = (code) => {
        if (!code.is_active) {
            return <Badge variant="destructive">Disabled</Badge>;
        }
        if (code.expiration_date) {
            const expires = new Date(code.expiration_date);
            if (expires < new Date()) {
                return <Badge variant="destructive">Expired</Badge>;
            }
        }
        if (code.uses_limit !== null && code.uses_count >= code.uses_limit) {
            return <Badge variant="secondary">Exhausted</Badge>;
        }
        return <Badge variant="default" className="bg-emerald-500">Active</Badge>;
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Invite Codes</h1>
                        <p className="text-muted-foreground">Manage beta access invite codes</p>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => { setShowBulkForm(!showBulkForm); setShowCreateForm(false); setBulkResults(null); }}
                            data-testid="bulk-create-btn"
                        >
                            <Users className="h-4 w-4 mr-2" />
                            Bulk Create
                        </Button>
                        <Button onClick={() => { setShowCreateForm(!showCreateForm); setShowBulkForm(false); }} data-testid="create-code-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Create Code
                        </Button>
                    </div>
                </div>

                {/* Bulk Create Form */}
                {showBulkForm && (
                    <Card className="bg-card border-border border-2 border-primary/20">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Users className="h-5 w-5 text-primary" />
                                Bulk Create Invite Codes
                            </CardTitle>
                            <CardDescription>Generate multiple single-use codes at once for your beta contractors</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleBulkCreate} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="bulk-count">Number of Codes</Label>
                                        <Input
                                            id="bulk-count"
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={bulkData.count}
                                            onChange={(e) => setBulkData({...bulkData, count: parseInt(e.target.value) || 1})}
                                            data-testid="bulk-count-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="bulk-prefix">Code Prefix (optional)</Label>
                                        <Input
                                            id="bulk-prefix"
                                            value={bulkData.prefix}
                                            onChange={(e) => setBulkData({...bulkData, prefix: e.target.value.toUpperCase()})}
                                            placeholder="e.g., BETA"
                                            className="uppercase"
                                            data-testid="bulk-prefix-input"
                                        />
                                        <p className="text-xs text-muted-foreground">Creates codes like BETA-A1B2C3</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="bulk-limit">Uses Per Code</Label>
                                        <Input
                                            id="bulk-limit"
                                            type="number"
                                            min="0"
                                            value={bulkData.uses_limit}
                                            onChange={(e) => setBulkData({...bulkData, uses_limit: parseInt(e.target.value) || 0})}
                                            data-testid="bulk-limit-input"
                                        />
                                        <p className="text-xs text-muted-foreground">1 = single-use (recommended)</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="bulk-note">Note</Label>
                                        <Input
                                            id="bulk-note"
                                            value={bulkData.note}
                                            onChange={(e) => setBulkData({...bulkData, note: e.target.value})}
                                            placeholder="e.g., SD Beta Launch May 2026"
                                            data-testid="bulk-note-input"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button type="submit" disabled={creating} data-testid="submit-bulk-btn">
                                        {creating ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <Ticket className="h-4 w-4 mr-2" />
                                        )}
                                        Create {bulkData.count} Codes
                                    </Button>
                                    <Button type="button" variant="outline" onClick={() => { setShowBulkForm(false); setBulkResults(null); }}>
                                        Cancel
                                    </Button>
                                </div>
                            </form>

                            {/* Bulk Results */}
                            {bulkResults && (
                                <div className="mt-6 p-4 bg-accent/30 rounded-lg">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-semibold text-foreground flex items-center gap-2">
                                            <Check className="h-4 w-4 text-emerald-500" />
                                            Created {bulkResults.length} Codes
                                        </h4>
                                        <Button size="sm" variant="outline" onClick={copyAllCodes}>
                                            <Copy className="h-3 w-3 mr-1" />
                                            Copy All
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-40 overflow-y-auto">
                                        {bulkResults.map((code, i) => (
                                            <div 
                                                key={code.id}
                                                className="flex items-center justify-between p-2 bg-background rounded border border-border text-sm"
                                            >
                                                <code className="font-mono text-xs">{code.code}</code>
                                                <button 
                                                    onClick={() => copyToClipboard(code.code)}
                                                    className="text-muted-foreground hover:text-foreground"
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Create Form */}
                {showCreateForm && (
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="text-lg">Create New Invite Code</CardTitle>
                            <CardDescription>Generate a code for new user signups</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleCreate} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="code">Code (optional)</Label>
                                        <Input
                                            id="code"
                                            value={newCode.code}
                                            onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                                            placeholder="Auto-generated if empty"
                                            className="uppercase"
                                            data-testid="new-code-input"
                                        />
                                        <p className="text-xs text-muted-foreground">Leave empty to auto-generate</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="uses_limit">Usage Limit</Label>
                                        <Input
                                            id="uses_limit"
                                            type="number"
                                            min="0"
                                            value={newCode.uses_limit}
                                            onChange={(e) => setNewCode({...newCode, uses_limit: parseInt(e.target.value) || 0})}
                                            placeholder="0 = unlimited"
                                            data-testid="uses-limit-input"
                                        />
                                        <p className="text-xs text-muted-foreground">0 = unlimited uses</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="expiration">Expiration Date (optional)</Label>
                                        <Input
                                            id="expiration"
                                            type="date"
                                            value={newCode.expiration_date}
                                            onChange={(e) => setNewCode({...newCode, expiration_date: e.target.value})}
                                            data-testid="expiration-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="note">Note (optional)</Label>
                                        <Input
                                            id="note"
                                            value={newCode.note}
                                            onChange={(e) => setNewCode({...newCode, note: e.target.value})}
                                            placeholder="e.g., For partner company X"
                                            data-testid="note-input"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button type="submit" disabled={creating} data-testid="submit-code-btn">
                                        {creating ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <Ticket className="h-4 w-4 mr-2" />
                                        )}
                                        Create Code
                                    </Button>
                                    <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-foreground">{codes.length}</div>
                            <p className="text-sm text-muted-foreground">Total Codes</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-emerald-500">
                                {codes.filter(c => c.is_active && (!c.uses_limit || c.uses_count < c.uses_limit)).length}
                            </div>
                            <p className="text-sm text-muted-foreground">Active</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-foreground">
                                {codes.reduce((acc, c) => acc + c.uses_count, 0)}
                            </div>
                            <p className="text-sm text-muted-foreground">Total Uses</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-foreground">
                                {codes.filter(c => c.uses_limit === null).length}
                            </div>
                            <p className="text-sm text-muted-foreground">Unlimited Codes</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Codes List */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Ticket className="h-5 w-5" />
                            All Invite Codes
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {codes.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No invite codes yet. Create one to get started.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {codes.map((code) => (
                                    <div 
                                        key={code.id}
                                        className="border border-border rounded-lg overflow-hidden"
                                    >
                                        <div 
                                            className="p-4 flex items-center justify-between cursor-pointer hover:bg-accent/30"
                                            onClick={() => setExpandedCode(expandedCode === code.id ? null : code.id)}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="font-mono text-lg font-bold text-foreground bg-accent px-3 py-1 rounded">
                                                    {code.code}
                                                </div>
                                                {getStatusBadge(code)}
                                                <span className="text-sm text-muted-foreground">
                                                    {getUsageLabel(code)} uses
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(code.code);
                                                    }}
                                                >
                                                    <Copy className="h-4 w-4" />
                                                </Button>
                                                {code.is_active && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDisable(code.id, code.code);
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                )}
                                                {expandedCode === code.id ? (
                                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </div>
                                        </div>
                                        
                                        {expandedCode === code.id && (
                                            <div className="p-4 bg-accent/20 border-t border-border space-y-3">
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-muted-foreground">Created:</span>
                                                        <p className="font-medium">{formatDate(code.created_at)}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Created by:</span>
                                                        <p className="font-medium">{code.created_by || 'Admin'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Expires:</span>
                                                        <p className="font-medium">
                                                            {code.expiration_date ? formatDate(code.expiration_date) : 'Never'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Note:</span>
                                                        <p className="font-medium">{code.note || '-'}</p>
                                                    </div>
                                                </div>
                                                
                                                {code.used_by && code.used_by.length > 0 && (
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                                            <Users className="h-4 w-4" />
                                                            Used by ({code.used_by.length})
                                                        </h4>
                                                        <div className="space-y-1">
                                                            {code.used_by.slice(0, 10).map((usage, idx) => (
                                                                <div key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                                                                    <Check className="h-3 w-3 text-emerald-500" />
                                                                    {usage.email} - {formatDate(usage.used_at)}
                                                                </div>
                                                            ))}
                                                            {code.used_by.length > 10 && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    ...and {code.used_by.length - 10} more
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default AdminInviteCodes;
